import { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import discountService from '../services/discountService';

/**
 * Discount Code Input Component
 * Allows users to enter and validate discount/coupon codes
 */
function DiscountCodeInput({ subtotal, onDiscountApplied, onDiscountRemoved, appliedDiscount }) {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Handle code input - auto-uppercase
  const handleCodeChange = (e) => {
    setCode(e.target.value.toUpperCase());
  };

  // Handle apply discount
  const handleApplyDiscount = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setIsValidating(true);

    try {
      const response = await discountService.validateDiscount(code, subtotal);

      if (response.success && response.data) {
        const discount = response.data;
        toast.success(`Discount "${discount.code}" applied successfully!`);
        onDiscountApplied(discount);
        setCode('');
      } else {
        toast.error(response.message || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Discount validation error:', error);
      const errorMessage = error.message || 'Failed to validate discount code';
      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  // Handle remove discount
  const handleRemoveDiscount = () => {
    onDiscountRemoved();
    toast.info('Discount code removed');
  };

  // If discount is already applied, show remove button
  if (appliedDiscount) {
    return (
      <div className="discount-code-applied mb-3 p-3" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '4px' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong className="text-primary">
              <i className="fas fa-check-circle mr-2"></i>
              Discount Applied: {appliedDiscount.code}
            </strong>
            <p className="mb-0 small text-muted mt-1">
              {appliedDiscount.type === 'PERCENTAGE' && `${appliedDiscount.value}% off`}
              {appliedDiscount.type === 'FIXED' && `£${(appliedDiscount.value / 100).toFixed(2)} off`}
              {appliedDiscount.type === 'FREE_SHIPPING' && 'Free shipping'}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={handleRemoveDiscount}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  // Show input form
  return (
    <div className="discount-code-input mb-3">
      <form onSubmit={handleApplyDiscount}>
        <div className="form-group">
          <label htmlFor="discountCode">Discount Code</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="discountCode"
              placeholder="Enter discount code"
              value={code}
              onChange={handleCodeChange}
              disabled={isValidating}
              style={{ textTransform: 'uppercase' }}
            />
            <div className="input-group-append">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isValidating || !code.trim()}
              >
                {isValidating ? 'Validating...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

DiscountCodeInput.propTypes = {
  subtotal: PropTypes.number.isRequired,
  onDiscountApplied: PropTypes.func.isRequired,
  onDiscountRemoved: PropTypes.func.isRequired,
  appliedDiscount: PropTypes.object
};

DiscountCodeInput.defaultProps = {
  appliedDiscount: null
};

export default DiscountCodeInput;
